'use client';
import { useTranslations } from 'next-intl';
import type { FC } from 'react';
import { CustomAccordion } from '@/app/components/shared/CustomAccordion';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION, TASK_SETTING_OPTION } from '@/app/constants';
import { useDevice, useUpgradeDialogs } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormTabType, TaskSettings } from '@/app/types';
import { cn, generateFromPage, isQ4HighResolution, validateAndCorrectSettings } from '@/app/utils';
import type { ViduV1TaskSettings } from '../../types';
import { AudioAndLipSyncOption } from './components/AudioAndLipSyncOption';
import {
  AmountOption,
  AspectRatioOptionSetting,
  CodecOptionSetting,
  CreationModeOptionSetting,
  DurationAndResolutionOption,
  DurationOptionSetting,
  ModelVersionOption,
  MovementAmplitude,
  MovementAmplitudeAndAspectRatioOption,
  NonPeakModeOption,
  ResolutionAndCodecOptionSetting,
  ResolutionOptionSetting,
  StyleOption,
  StylePreferenceOption,
} from './components/SettingsOptions';

type TransitionType = ViduV1TaskSettings['transition'];

interface SettingsProps {
  className?: string;
  settings?: TaskSettings;
  updateSettings: (newValues: Partial<TaskSettings>) => void;
  includedOptions: TASK_SETTING_OPTION[];
  type: Exclude<FormTabType, 'scene'>;
}

export const Settings: FC<SettingsProps> = ({ className, settings, updateSettings, includedOptions, type }) => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const modelVersion = useStore((state) => state[`${type}FormParams`].modelVersion);
  const { handleOpenPlanDialog } = useUpgradeDialogs();
  const updateResolution = (resolution: string, extra: Partial<TaskSettings> = {}) => {
    updateSettings({
      ...extra,
      resolution,
      ...(isQ4HighResolution(modelVersion, resolution) ? { schedule_mode: 'normal' } : {}),
    });
  };
  const visibleIncludedOptions = isQ4HighResolution(modelVersion, settings?.resolution)
    ? includedOptions.filter((option) => option !== TASK_SETTING_OPTION.NonPeakMode)
    : includedOptions;

  const optionComponents = {
    [TASK_SETTING_OPTION.ModelVersion]: <ModelVersionOption key="modelVersion" type={type} />,
    [TASK_SETTING_OPTION.Style]: (
      <StyleOption
        key="style"
        value={settings?.style}
        onChange={(val) => {
          updateSettings({ style: val });
        }}
      />
    ),
    [TASK_SETTING_OPTION.StylePreference]: (
      <StylePreferenceOption
        key="stylePreference"
        value={settings?.transition}
        onChange={(val) => updateSettings({ transition: val as Exclude<TransitionType, 'unspecified'> })}
      />
    ),
    [TASK_SETTING_OPTION.MovementAmplitude]: (
      <MovementAmplitude
        key="movementAmplitude"
        value={settings?.movement_amplitude}
        onChange={(val) => updateSettings({ movement_amplitude: val })}
      />
    ),
    [TASK_SETTING_OPTION.Amount]: (
      <AmountOption
        key="amount"
        value={settings?.sample_count ?? 1}
        onChange={(val) => {
          updateSettings({ sample_count: val });
        }}
        onShouldUpgrade={(key) => handleOpenPlanDialog(generateFromPage({ modelVersion, type, key }))}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.NonPeakMode]: (
      <NonPeakModeOption
        key="nonPeakMode"
        value={settings?.schedule_mode === 'nopeak'}
        onChange={(val) => {
          const nextSettings: Partial<TaskSettings> = { schedule_mode: val ? 'nopeak' : 'normal' };
          if (val && settings?.with_audio) {
            nextSettings.with_audio = false;
            nextSettings.lip_sync = false;
            toast({
              type: 'info',
              message: t('popup.toast.invalidSettingForOffPeakMode'),
            });
          }
          updateSettings(nextSettings);
        }}
        onOpenPlanDetailDialog={() => handleOpenPlanDialog('off-peak mode')}
      />
    ),
    [TASK_SETTING_OPTION.AudioAndLipSync]: (
      <AudioAndLipSyncOption
        key="audioAndLipSync"
        value={{ with_audio: settings?.with_audio ?? false, lip_sync: settings?.lip_sync ?? false }}
        onChange={(val) => {
          const nextSettings: Partial<TaskSettings> = { with_audio: val?.with_audio, lip_sync: val?.lip_sync };
          if (settings?.schedule_mode === 'nopeak') {
            nextSettings.schedule_mode = 'normal';
            toast({
              type: 'info',
              message: t('popup.toast.invalidSettingForOffPeakMode'),
            });
          }
          updateSettings(nextSettings);
        }}
      />
    ),
    [TASK_SETTING_OPTION.Codec]: (
      <CodecOptionSetting
        key="codec"
        value={settings?.codec ?? 'h265'}
        type={type}
        onChange={(val) => updateSettings({ codec: val as Exclude<TaskSettings['codec'], undefined> })}
        onShouldUpgrade={(type) => handleOpenPlanDialog(`settings ${type}`)}
      />
    ),
    [TASK_SETTING_OPTION.Resolution]: (
      <ResolutionOptionSetting
        key="resolution"
        value={settings?.resolution ?? '1080p'}
        onChange={(val) => {
          // 2.0 模型 8s 只支持 720p
          if (modelVersion === MODEL_VERSION['2.0']) {
            // 当前切换的是 resolution，判断 duration
            if (val !== '720p' && settings?.duration === 8) {
              toast({
                message: t('create.createForm.settings.resolution.unSupportToast'),
              });
              updateResolution(val, { duration: 4 });
              return;
            }
          }
          updateResolution(val);
        }}
        onShouldUpgrade={(type) => handleOpenPlanDialog(`settings ${type}`)}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.ResolutionAndCodec]: (
      <ResolutionAndCodecOptionSetting
        key="resolutionAndCodec"
        value={{
          resolution: settings?.resolution ?? '1080p',
          codec: settings?.codec ?? 'h265',
        }}
        onChange={(val) => {
          // 2.0 模型 8s 只支持 720p
          if (modelVersion === MODEL_VERSION['2.0']) {
            // 当前切换的是 resolution，判断 duration
            if (val.resolution !== '720p' && settings?.duration === 8) {
              toast({
                message: t('create.createForm.settings.resolution.unSupportToast'),
              });
              updateResolution(val.resolution, {
                duration: 4,
                codec: val.codec as 'h265' | 'h264_pro' | 'h265_pro',
              });
              return;
            }
          }
          updateResolution(val.resolution, { codec: val.codec as 'h265' | 'h264_pro' | 'h265_pro' });
        }}
        onShouldUpgrade={(type) => handleOpenPlanDialog(`settings ${type}`)}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.DurationAndResolution]: (
      <DurationAndResolutionOption
        key="durationAndResolution"
        value={{ duration: settings?.duration ?? 0, resolution: settings?.resolution ?? '720p' }}
        onChange={(val) => {
          // 2.0 模型 8s 只支持 720p
          if (modelVersion === MODEL_VERSION['2.0']) {
            // 当前切换的是 resolution,时判断 duration
            if (val.resolution !== '720p' && settings?.duration === 8) {
              toast({
                message: t('create.createForm.settings.resolution.unSupportToast'),
              });
              updateResolution(val.resolution, { duration: 4 });
            } else if (
              //当前切换的是 duration,时判断 分辨率
              settings?.resolution !== '720p' &&
              val.duration === 8
            ) {
              toast({
                message: t('create.createForm.settings.duration.unSupportToast'),
              });
              updateSettings({ duration: val.duration, resolution: '720p' });
            } else {
              // 其他情况直接更新
              updateResolution(val.resolution, { duration: val.duration });
            }
          } else {
            updateResolution(val.resolution, { duration: val.duration });
          }
        }}
        onShouldUpgrade={(type) => handleOpenPlanDialog(`settings ${type}`)}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio]: (
      <MovementAmplitudeAndAspectRatioOption
        key="movementAmplitudeAndAspectRatio"
        value={{
          movementAmplitude: settings?.movement_amplitude ?? 'auto',
          aspectRatio: settings?.aspect_ratio ?? '16:9',
        }}
        onChange={(val) => updateSettings({ movement_amplitude: val.movementAmplitude, aspect_ratio: val.aspectRatio })}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.AspectRatio]: (
      <AspectRatioOptionSetting
        key="aspectRatio"
        value={settings?.aspect_ratio ?? '16:9'}
        onChange={(val) => updateSettings({ aspect_ratio: val })}
        type={type}
      />
    ),
    [TASK_SETTING_OPTION.CreationMode]: (
      <CreationModeOptionSetting
        key="creationMode"
        value={settings?.transition}
        onChange={(val) => {
          const draft = { ...settings, transition: val } as TaskSettings;
          const corrected = validateAndCorrectSettings({
            version: modelVersion,
            newSettings: draft,
            type,
            t,
          });
          updateSettings({
            transition: corrected.transition,
            duration: corrected.duration,
            resolution: corrected.resolution,
          });
        }}
        type={type}
        onShouldUpgrade={(key) => handleOpenPlanDialog(generateFromPage({ modelVersion, type, key }))}
      />
    ),
    [TASK_SETTING_OPTION.Duration]: (
      <DurationOptionSetting
        key="duration"
        value={settings?.duration}
        onChange={(val) => updateSettings({ duration: val })}
        type={type}
        display="segment"
        onShouldUpgrade={(key) => handleOpenPlanDialog(generateFromPage({ modelVersion, type, key }))}
      />
    ),
  };

  return (
    <div className={cn('shrink-0 rounded-12 bg-[#1A1C20] p-4 sm:p-3', className)}>
      <CustomAccordion
        title={isMobile ? t('create.createForm.settings.title') : ''}
        defaultExpanded={!isMobile}
        isUseAccordion={isMobile}
      >
        <div className="flex flex-col md:gap-3">
          {visibleIncludedOptions.map((option) => {
            const component = optionComponents[option as keyof typeof optionComponents];
            if (!component) return null;
            return (
              <div
                key={option}
                className="after:my-3 after:block after:h-px after:w-full after:bg-system-white04 after:content-[''] last:after:hidden md:after:hidden"
              >
                {component}
              </div>
            );
          })}
        </div>
      </CustomAccordion>
    </div>
  );
};
